import React from 'react';
import Papa from 'papaparse';
import { FormLabel, ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
import { grey } from '@mui/material/colors';

import { ConfirmDialog, DropFileInput } from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import { bytesToMB } from '@occc-app/lib/format';

const EXCEL_HEX_STRINGS = ['504b0304', 'D0CF11E0'];

export type InstanceState = {
  columnNames: string[];
  file?: File;
  showHeaderModal: boolean;
  hasHeaders: string;
  reader?: FileReader;
  selectColumnModalOpen: boolean;
};

type Props = {
  delimiter?: string;
  formLabelCopy: string;
  initiateCSVUpload: (state: InstanceState) => void;
  onCancel?: () => void;
  onParseComplete?: (
    results: { data: object[] | string[][] },
    file: File,
    parseConfig: { header: boolean },
  ) => void;
  onDrop?: (files: File) => void;
  parentAdditionalModalContext?: (state: InstanceState) => JSX.Element | false;
  parentContextDisableAction?: (state: InstanceState) => boolean;
  parentResetModalState?: () => void;
  uploadCopy?: string;
};

const initialState: InstanceState = {
  columnNames: [],
  showHeaderModal: false,
  hasHeaders: 'neither',
  selectColumnModalOpen: false,
};

const MAX_FILE_SIZE_MB = 500;
export class CSVUploader extends React.Component<Props, {}> {
  state: InstanceState = initialState;

  UNSAFE_componentWillMount() {
    const reader = new FileReader();
    reader.onload = () => {
      const buffer = reader.result;
      if (!buffer || typeof buffer === 'string') {
        return;
      }

      const view = new DataView(buffer);
      const leadingHexBytes = view.getUint32(0, false).toString(16);

      if (EXCEL_HEX_STRINGS.includes(leadingHexBytes)) {
        this.handleErrorMessage();
      } else {
        const { onDrop } = this.props;

        if (onDrop) {
          onDrop(this.state.file!);
        } else {
          this.defaultOnDrop();
        }
      }
    };

    this.setState({
      reader,
    });
  }

  handleErrorMessage = (message?: string) =>
    snackbar.error(
      message ||
        'Only a single CSV file is accepted. Please ensure the file has the correct format.',
    );

  defaultOnDrop = () => {
    this.setState({
      showHeaderModal: true,
    });
  };

  defaultParse = () => {
    if (!this.state.file) {
      return;
    }

    const { delimiter, onParseComplete } = this.props;
    const header = this.state.hasHeaders === 'yes';

    const config = {
      delimiter: delimiter || ',',
      complete: (results: any, file: any) => {
        if (!onParseComplete) {
          throw Error('onComplete must be defined if using defaultOnDrop');
        }
        onParseComplete(results, file, config);
      },
      header,
    };
    Papa.parse(this.state.file, config);
  };

  handleIncludeHeadersIndicated = (_e: React.FormEvent, value: string[]) => {
    if (!this.state.file) {
      return;
    }

    const hasHeaders = value[0];
    this.setState({ hasHeaders });
    const header = hasHeaders === 'yes';
    Papa.parse(this.state.file, {
      header,
      preview: 1,
      complete: (results) => {
        // as any, because it's not typed
        // papaparse switched from any to unknown for type safety
        const row = results.data[0] as any;
        const columnNames = header
          ? row
            ? Object.keys(row).filter((k) => k !== '__parsed_extra')
            : []
          : row.filter((e: any) => e).map((c: any, i: number) => `Column #${i + 1}`);
        this.setState({ columnNames });
      },
    });
  };

  initiateCSVUpload = () => {
    const { initiateCSVUpload } = this.props;

    initiateCSVUpload ? initiateCSVUpload(this.state) : this.defaultParse();
    this.resetModalState();
  };

  onDrop = (files: File[]) => {
    if (files.length > 1) {
      this.handleErrorMessage();
      return;
    }

    const file = files[0];
    const isValidSize = bytesToMB(file.size) <= MAX_FILE_SIZE_MB;

    if (isValidSize) {
      const slice = file.slice(0, 4);
      this.state.reader!.readAsArrayBuffer(slice);
      // Need to set file in state here since we won't have access to it in the file reader
      this.setState({ file });
    } else {
      this.handleErrorMessage(`File size shouldn't exceed ${MAX_FILE_SIZE_MB}MB`);
    }
  };

  resetModalState = () => {
    this.setState(initialState);
    if (this.props.parentResetModalState) {
      this.props.parentResetModalState();
    }
  };

  renderAdditionalModalContext() {
    const { parentAdditionalModalContext } = this.props;

    if (!parentAdditionalModalContext) {
      return null;
    }

    return parentAdditionalModalContext(this.state);
  }

  render() {
    const { formLabelCopy, onCancel, parentContextDisableAction, uploadCopy } = this.props;
    const { hasHeaders } = this.state;

    const headerInfoProvided = hasHeaders !== 'neither';
    const disableAction =
      !headerInfoProvided || (parentContextDisableAction && parentContextDisableAction(this.state));

    return (
      <Box>
        <DropFileInput
          label={formLabelCopy}
          onDrop={this.onDrop}
          onFileDialogCancel={onCancel}
          uploadCopy={uploadCopy}
        />
        <ConfirmDialog
          dialogTitle='CSV file(s) upload'
          open={this.state.showHeaderModal}
          onClose={this.resetModalState}
          confirmButtonText='Upload'
          confirmButtonProps={{
            onClick: this.initiateCSVUpload,
            disabled: disableAction,
          }}
        >
          <FormLabel
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              color: grey[800],
              fontSize: theme.typography.subtitle2.fontSize,
              lineHeight: theme.typography.subtitle2.lineHeight,
              fontWeight: 500,
              px: 0.5,
              mb: 1,
            })}
          >
            Please indicate if the CSV file(s) include Headers.
          </FormLabel>
          <ToggleButtonGroup
            aria-label='pointRounding'
            onChange={this.handleIncludeHeadersIndicated}
          >
            <ToggleButton value='yes' selected={hasHeaders === 'yes'}>
              Yes
            </ToggleButton>
            <ToggleButton value='no' selected={hasHeaders === 'no'}>
              No
            </ToggleButton>
          </ToggleButtonGroup>
          {this.renderAdditionalModalContext()}
        </ConfirmDialog>
      </Box>
    );
  }
}
